'use client';

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';
import SkeletonBusiness from '@components/skeletons/SkeletonBusiness';
import { MapIcon, ListBulletIcon, MagnifyingGlassIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { transformVendorCardData, transformDirectoryCardData } from '@utils/transformations';
import React from 'react';
import MainLayout from '@layout/MainLayout';
import VendorCard from '@components/elements/VendorCard';
import DirectoryCard from '@components/elements/DirectoryCard';
import { useSearch } from '@contexts/search';
import { useFullScreenSearch } from '@contexts/fullscreensearch';
import { trackEvent } from '../../lib/analytics/posthog';
import { EVENTS } from '../../lib/analytics/events';
import { FilterChips, TodayChip, ViewTabs, sortCategories, categoryLabel } from './components/Filters';
import { boundsAround, boundsAreClose, boundsQuery, mapZoomFor } from '../../lib/search-bounds';
import { defaultUbication } from '@utils/maps';
import { highlightStore } from './components/highlight-store';
import WorksGrid from './components/WorksGrid';
import { todayInArgentina, useNextAvailableDates } from '@contexts/next-available';

// mapbox-gl pesa ~250 KB comprimido: va en su propio chunk, después de que la lista ya se pintó.
const loadMapView = () => import('./components/MapView');
const MapView = dynamic(loadMapView, {
  ssr: false,
  loading: () => <div className="h-full w-full bg-[#eef1f5]" />
});

// Mismo corte que el de Tailwind `md`: desde ahí el mapa ocupa todo y el panel flota encima.
const SPLIT_VIEW_QUERY = '(min-width: 767px)';
// Ancho que tapa el panel de resultados (456 + márgenes de 16): el mapa busca y centra en el resto.
const PANEL_INSET = 488;
// Alto de la barra de arriba del teléfono (styles/map.css, --explore-bar-h).
const PHONE_BAR_HEIGHT = 162;
// Barra de vidrio del sitio en escritorio (16 de margen + 64 de alto + aire): los pines con foto no van debajo.
const DESKTOP_BAR_HEIGHT = 88;

const API_URL = process.env.NEXT_PUBLIC_CYCLONE_API_URL;

const fetcher = (url) =>
  fetch(url).then((res) => {
    if (!res.ok) throw new Error(`API error: ${res.status}`);
    return res.json();
  });

/**
 * Simple debounce utility (no lodash dependency).
 * @param {Function} fn - The function to debounce.
 * @param {number} delay - Delay in milliseconds.
 * @returns {Function} Debounced function.
 */
function debounce(fn, delay) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

const EmptyState = ({ query }) => (
  <div className="flex flex-col items-center justify-center py-24 px-6 text-center fade-in">
    <div className="w-14 h-14 rounded-2xl bg-white border border-[var(--line)] flex items-center justify-center mb-5">
      <MagnifyingGlassIcon className="h-6 w-6 text-slate-400" />
    </div>
    <h2 className="text-[17px] font-bold text-[var(--ink)] mb-2">No encontramos resultados</h2>
    <p className="text-[13px] text-slate-500 max-w-xs leading-relaxed font-medium">
      {query ? (
        <>
          No hay negocios para &ldquo;{query}&rdquo; en esta zona. Prob&aacute; ampliando el radio o cambiando
          los t&eacute;rminos.
        </>
      ) : (
        <>No hay negocios en esta zona. Prob&aacute; moviendo el mapa o buscando en otra ubicaci&oacute;n.</>
      )}
    </p>
  </div>
);

const formatCount = (n) => n.toLocaleString('es-AR');

const ResultsHeader = ({ vendorCount, directoryCount, query, isValidating }) => {
  const totalCount = vendorCount + directoryCount;
  return (
    <div className="explore__note" aria-live="polite">
      <span
        aria-hidden="true"
        className={clsx('results-spinner flex-shrink-0', isValidating && 'is-active')}
      />
      <p className="truncate">
        {totalCount > 0 ? (
          <>
            <span className="text-[var(--ink)] font-semibold tabular-nums">{formatCount(vendorCount)}</span>{' '}
            en Ágora
            {directoryCount > 0 && (
              <>
                {' · '}
                <span className="text-[var(--ink)] font-semibold tabular-nums">
                  {formatCount(directoryCount)}
                </span>{' '}
                en el directorio
              </>
            )}
            {query && (
              <>
                {' '}
                para <span className="text-[var(--ink)] font-semibold">&ldquo;{query}&rdquo;</span>
              </>
            )}
          </>
        ) : isValidating ? (
          'Buscando negocios…'
        ) : (
          'Sin resultados en esta zona'
        )}
      </p>
    </div>
  );
};

const EndOfResults = () => (
  <p className="py-10 text-center text-[12px] font-medium text-slate-400">
    No hay más resultados en esta zona
  </p>
);

/**
 * Interleave vendor and directory results: 3 vendors, then 1 directory, repeat.
 * Each item gets a _compositeId for bidirectional hover/select tracking.
 * @param {Array} vendors - Transformed vendor card data.
 * @param {Array} directory - Transformed directory card data.
 * @returns {Array} Interleaved array with _compositeId on each item.
 */
const interleaveResults = (vendors, directory) => {
  const result = [];
  let vi = 0;
  let di = 0;
  while (vi < vendors.length || di < directory.length) {
    for (let i = 0; i < 3 && vi < vendors.length; i++, vi++) {
      result.push({ ...vendors[vi], _compositeId: `agora-${vendors[vi].id}` });
    }
    if (di < directory.length) {
      result.push({ ...directory[di], _compositeId: `directory-${directory[di].id}` });
      di++;
    }
  }
  return result;
};

const PAGE_SIZE = 18;

const SWR_LIST_OPTIONS = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  // cada página nueva del scroll volvía a pedir también la primera.
  revalidateFirstPage: false,
  // Al mover el mapa la lista anterior queda (atenuada) hasta que llega la nueva: antes se vaciaba
  // y volvían los esqueletos con cada arrastre.
  keepPreviousData: true
};

const highlight = (id) => () => highlightStore.set(id);
const clearHighlight = () => highlightStore.set(null);

const NO_ITEMS = [];

/** Volver: a la página anterior si vino de market; si entró directo, al inicio. */
function useGoBack() {
  const router = useRouter();
  return () => {
    const cameFromHere =
      typeof document !== 'undefined' && document.referrer.startsWith(window.location.origin);
    if (cameFromHere && window.history.length > 1) router.back();
    else router.push('/');
  };
}

const NegociosClient = () => {
  const {
    params: { query, lat, lng, distance },
    locationLabel,
    isReady: areParamsReady
  } = useSearch();
  const { showSearchFullScreen, setShowSearchFullScreen } = useFullScreenSearch();
  const goBack = useGoBack();

  // --- State ---
  // Caja que informó el mapa (arrastre, zoom o vuelo). Mientras no exista se usa la caja
  // calculada desde la URL: en el teléfono la vista de lista no monta el mapa.
  const [mapBounds, setMapBounds] = useState(null); // { swLat, swLng, neLat, neLng }
  const [viewportSize, setViewportSize] = useState(null); // tamaño del mapa, visible o no
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [isNewSearch, setIsNewSearch] = useState(false);
  // Teléfono: el mapa es una capa sobre la lista. Se monta la primera vez que se abre y después
  // queda montado: alternar lista/mapa es instantáneo y no vuelve a cargar el estilo ni los tiles.
  const [showMapOnPhone, setShowMapOnPhone] = useState(false);
  const [isMapMounted, setIsMapMounted] = useState(false);
  // Rubro elegido ({ id, name }): filtra por `category_id`; el texto escrito sigue yendo como `search`.
  const [selectedCategory, setSelectedCategory] = useState(null);
  // «Turnos hoy»: solo negocios con un turno libre hoy (según su próximo turno).
  const [todayOnly, setTodayOnly] = useState(false);
  // Negocios (tarjetas) o Trabajos (fotos con precio). `?vista=trabajos` abre directo en fotos.
  const [view, setView] = useState('negocios');
  const [maxPrice, setMaxPrice] = useState('');
  const [isSplit, setIsSplit] = useState(false);

  const categoryId = selectedCategory?.id || null;
  // El directorio (Google) no tiene rubros de Ágora: ahí el rubro sigue buscándose por nombre.
  const directoryQuery = selectedCategory?.name || query;

  const prevQueryRef = useRef(query);
  const scrollContainerRef = useRef();
  const mapPanelRef = useRef();
  const cardRefsMap = useRef(new Map());
  const loaderRef = useRef();

  const { data: categoriesData } = useSWR(`${API_URL}/categories?level=2&category_parent_id=326`, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false
  });
  const categories = useMemo(
    () => (Array.isArray(categoriesData) ? sortCategories(categoriesData) : categoriesData ? [] : null),
    [categoriesData]
  );

  // En pantallas con lista + mapa, el mapa se monta enseguida (en su chunk aparte). En el teléfono
  // solo se baja el chunk en un momento ocioso: «Ver mapa» ya no espera la descarga.
  useEffect(() => {
    const mq = window.matchMedia(SPLIT_VIEW_QUERY);
    const sync = () => {
      setIsSplit(mq.matches);
      if (mq.matches) setIsMapMounted(true);
    };
    sync();
    if (new URLSearchParams(window.location.search).get('vista') === 'trabajos') setView('trabajos');
    if (!mq.matches) {
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
      idle(() => loadMapView());
    }
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Tamaño que tiene (o tendría) el mapa: toda la pantalla (va debajo de la barra de vidrio) menos
  // el panel en escritorio.
  // Se mide una sola vez (no en cada resize): en el teléfono la barra del navegador cambia el
  // alto al hacer scroll y cada cambio volvería a pedir la lista desde cero.
  useEffect(() => {
    const split = window.matchMedia(SPLIT_VIEW_QUERY).matches;
    setViewportSize({
      width: Math.max(1, window.innerWidth - (split ? PANEL_INSET : 0)),
      height: Math.max(1, window.innerHeight)
    });
  }, []);

  // Caja inicial: la que mostraría el mapa centrado en la ubicación de la URL (o la de siempre).
  const searchLat = parseFloat(lat) || defaultUbication.lat;
  const searchLng = parseFloat(lng) || defaultUbication.long;
  const initialBounds = useMemo(() => {
    if (!areParamsReady || !viewportSize) return null;
    return boundsAround({
      lat: searchLat,
      lng: searchLng,
      zoom: mapZoomFor(distance),
      width: viewportSize.width,
      height: viewportSize.height
    });
  }, [areParamsReady, viewportSize, searchLat, searchLng, distance]);

  // Una ubicación nueva en la URL descarta la caja del mapa: hasta que el mapa vuele y
  // reporte la suya, se busca alrededor de la ubicación nueva.
  const locationKey = `${lat}|${lng}|${distance}`;
  const prevLocationKeyRef = useRef(locationKey);
  if (prevLocationKeyRef.current !== locationKey) {
    prevLocationKeyRef.current = locationKey;
    if (mapBounds) setMapBounds(null);
  }

  const searchBounds = mapBounds || initialBounds;
  const searchBoundsRef = useRef(searchBounds);
  useEffect(() => {
    searchBoundsRef.current = searchBounds;
  }, [searchBounds]);
  const boundsParams = searchBounds ? boundsQuery(searchBounds) : null;

  const filterParams = `${categoryId ? `&category_id=${categoryId}` : ''}${query ? `&search=${encodeURIComponent(query)}` : ''}`;

  // --- Pins SWR (lightweight, all pins in viewport) ---
  const pinsKey = boundsParams ? `${API_URL}/marketplace/pins?${boundsParams}${filterParams}` : null;

  const { data: pinsData } = useSWR(pinsKey, fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true,
    dedupingInterval: 2000
  });

  // --- Vendor SWR (card list, paginated, bounding box) ---
  const getKey = (pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData.vendors?.length) return null;
    if (!boundsParams) return null;
    let url = `${API_URL}/vendors/marketplace/published?page=${pageIndex + 1}&limit=${PAGE_SIZE}`;
    url += `&${boundsParams}${filterParams}`;
    if (maxPrice) url += `&max_price=${maxPrice}`;
    return url;
  };

  const { data, error, size, setSize, isValidating } = useSWRInfinite(getKey, fetcher, SWR_LIST_OPTIONS);

  // --- Directory SWR (card list, paginated, bounding box) ---
  const getDirectoryKey = (pageIndex, previousPageData) => {
    if (previousPageData && !previousPageData.listings?.length) return null;
    if (!boundsParams) return null;
    // Con «Hasta $X» o «Turnos hoy», las fichas de Google (sin precio ni turnos) no se muestran: no se piden.
    if (maxPrice || todayOnly) return null;
    let url = `${API_URL}/directory/marketplace?page=${pageIndex + 1}&limit=${PAGE_SIZE}`;
    url += `&${boundsParams}`;
    if (directoryQuery) url += `&search=${encodeURIComponent(directoryQuery)}`;
    return url;
  };

  const {
    data: dirData,
    size: dirSize,
    setSize: setDirSize,
    isValidating: isDirValidating
  } = useSWRInfinite(getDirectoryKey, fetcher, SWR_LIST_OPTIONS);

  // --- Synchronous query-change detection ---
  if (prevQueryRef.current !== query && !isNewSearch) {
    prevQueryRef.current = query;
    setIsNewSearch(true);
  }

  // Side effects when a new search starts: reset pagination, scroll to top
  useEffect(() => {
    if (isNewSearch) {
      setSize(1);
      setDirSize(1);
      scrollContainerRef.current?.scrollTo({ top: 0 });
    }
  }, [isNewSearch, setSize, setDirSize]);

  // Clear loading states when fresh data arrives
  useEffect(() => {
    if (data && data.length > 0 && !isValidating) {
      setIsNewSearch(false);
    }
  }, [data, isValidating]);

  // Al salir de /negocios no queda un pin resaltado para la próxima vez (el store vive en el módulo).
  useEffect(() => () => highlightStore.set(null), []);

  // Clear selectedItemId when mapBounds changes (avoid stale selections)
  useEffect(() => {
    setSelectedItemId(null);
  }, [mapBounds]);

  // Track search results
  const hasTrackedResults = useRef(false);
  useEffect(() => {
    if (!data || isValidating || isNewSearch) return;
    if (hasTrackedResults.current) return;
    hasTrackedResults.current = true;
    const vc = data[0]?.total_count || 0;
    const dc = dirData?.[0]?.total_count || 0;
    const isEmpty = vc + dc === 0;
    trackEvent(EVENTS.SEARCH_RESULTS_VIEWED, {
      query,
      vendor_count: vc,
      directory_count: dc,
      total_count: vc + dc,
      is_empty: isEmpty
    });
    if (isEmpty) trackEvent(EVENTS.SEARCH_RESULTS_EMPTY, { query });
  }, [data, dirData, isValidating, isNewSearch, query]);

  // Reset tracking on new search
  useEffect(() => {
    if (isNewSearch) hasTrackedResults.current = false;
  }, [isNewSearch]);

  // --- Vendor pagination ---
  const totalFetchedVendors = data ? data.reduce((acc, page) => acc + (page.vendors?.length || 0), 0) : 0;
  const totalAvailableVendors = data?.[0]?.total_count || 0;
  const isVendorsReachingEnd = totalFetchedVendors >= totalAvailableVendors;

  // --- Directory pagination ---
  const directoryActive = !maxPrice && !todayOnly;
  const totalFetchedDir = dirData ? dirData.reduce((acc, page) => acc + (page.listings?.length || 0), 0) : 0;
  const totalAvailableDir = directoryActive ? dirData?.[0]?.total_count || 0 : 0;
  const isDirReachingEnd = !directoryActive || totalFetchedDir >= totalAvailableDir;

  const isReachingEnd = isVendorsReachingEnd && isDirReachingEnd;

  // --- Single infinite scroll observer (loads more from whichever source has remaining) ---
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isNewSearch) {
          let loadsMore = false;
          if (!isVendorsReachingEnd && !isValidating) {
            setSize((prev) => prev + 1);
            loadsMore = true;
          }
          if (!isDirReachingEnd && !isDirValidating) {
            setDirSize((prev) => prev + 1);
            loadsMore = true;
          }
          // El centinela ahora se ve aunque no haya más para traer: solo cuenta si pidió otra página.
          if (loadsMore) {
            trackEvent(EVENTS.DISCOVERY_INFINITE_SCROLL, {
              page_number: size,
              items_loaded_total: totalFetchedVendors + totalFetchedDir
            });
          }
        }
      },
      // El centinela quedaba a fracciones de px del borde del panel con scroll: con threshold 1
      // nunca estaba entero a la vista y el scroll infinito se cortaba en la segunda página.
      // Se observa contra el propio panel y se dispara un poco antes de llegar al final.
      { root: scrollContainerRef.current || null, rootMargin: '0px 0px 600px 0px', threshold: 0 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
    // `size` y los totales solo van en el evento de analytics: con ellos en las dependencias el
    // observer se rearmaría con cada página que llega y, al rearmarse con el centinela a la vista,
    // pediría otra página de más.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    setSize,
    setDirSize,
    isVendorsReachingEnd,
    isDirReachingEnd,
    isValidating,
    isDirValidating,
    isNewSearch,
    // Al volver de Trabajos el centinela es otro nodo: hay que observar ese.
    view
  ]);

  // --- Debounced bounds handler ---
  const mapTrackTimerRef = useRef(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleBoundsChanged = useCallback(
    debounce(({ swLat, swLng, neLat, neLng }) => {
      const next = { swLat, swLng, neLat, neLng };
      // El mapa recién cargado (o después de volar a la ubicación de la URL) muestra casi la
      // misma caja que ya se pidió: no repetir los tres pedidos por unos píxeles de diferencia.
      if (boundsAreClose(searchBoundsRef.current, next)) return;
      setMapBounds(next);
      // La tarjeta bajo el mouse puede desaparecer con la lista nueva sin disparar mouseleave.
      highlightStore.set(null);
      setSize(1);
      setDirSize(1);
      scrollContainerRef.current?.scrollTo({ top: 0 });
      // Track map bounds with 2s debounce to avoid noise
      clearTimeout(mapTrackTimerRef.current);
      mapTrackTimerRef.current = setTimeout(() => {
        trackEvent(EVENTS.DISCOVERY_MAP_BOUNDS_CHANGED, {
          center_lat: (swLat + neLat) / 2,
          center_lng: (swLng + neLng) / 2
        });
      }, 2000);
    }, 250),
    []
  );

  // --- Pin select handler (scroll to card) ---
  const handlePinSelect = useCallback((compositeId) => {
    trackEvent(EVENTS.DISCOVERY_MAP_PIN_CLICKED, { vendor_id: compositeId });
    setSelectedItemId(compositeId);
    cardRefsMap.current.get(compositeId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const toggleMapOnPhone = () => {
    trackEvent(EVENTS.DISCOVERY_VIEW_TOGGLED, { view: showMapOnPhone ? 'list' : 'map' });
    setIsMapMounted(true);
    setShowMapOnPhone((prev) => !prev);
  };

  // --- Transform + interleave ---
  const vendorsData = useMemo(
    () => transformVendorCardData(data ? data.flatMap((page) => page.vendors || []) : []) || [],
    [data]
  );

  const directoryData = useMemo(
    () =>
      directoryActive
        ? transformDirectoryCardData(dirData ? dirData.flatMap((page) => page.listings || []) : []) || []
        : NO_ITEMS,
    [dirData, directoryActive]
  );

  // «Turnos hoy»: se piden los próximos turnos de la lista cargada y quedan los que tienen hoy
  // (mientras llega el dato, la tarjeta se queda).
  const todayUsernames = useMemo(
    () => (todayOnly ? vendorsData.map((v) => v.username).filter(Boolean) : NO_ITEMS),
    [todayOnly, vendorsData]
  );
  const nextDates = useNextAvailableDates(todayUsernames);
  const today = todayInArgentina();
  const visibleVendors = todayOnly
    ? vendorsData.filter(
        (v) =>
          nextDates[v.username] === undefined || (nextDates[v.username] && nextDates[v.username] <= today)
      )
    : vendorsData;

  const interleavedItems = useMemo(
    () => interleaveResults(visibleVendors, directoryData),
    [visibleVendors, directoryData]
  );

  // Con «Hasta $X», el mapa muestra los negocios de Ágora que entran (las fichas de Google no tienen precio).
  const pins = useMemo(() => {
    const all = pinsData?.pins;
    if (!all) return all;
    if (!maxPrice && !todayOnly) return all;
    return all.filter(
      (pin) => pin.source === 'agora' && (!maxPrice || (pin.min_price && pin.min_price <= Number(maxPrice)))
    );
  }, [pinsData, maxPrice, todayOnly]);

  // Para la ficha del pin elegido: las fotos y servicios que ya trajo la lista.
  const vendorsById = useMemo(() => new Map(vendorsData.map((v) => [`agora-${v.id}`, v])), [vendorsData]);

  const resetLists = () => {
    setSize(1);
    setDirSize(1);
    scrollContainerRef.current?.scrollTo({ top: 0 });
  };

  const selectView = (next) => {
    if (next === view) return;
    trackEvent(EVENTS.DISCOVERY_VIEW_TOGGLED, { view: next });
    setView(next);
    scrollContainerRef.current?.scrollTo({ top: 0 });
  };

  const filterProps = {
    categories,
    selectedCategory,
    onSelectCategory: (cat) => {
      setSelectedCategory(cat);
      resetLists();
    },
    todayOnly,
    onToggleToday: () => {
      setTodayOnly((v) => !v);
      resetLists();
    },
    maxPrice,
    onPriceChange: (value) => {
      setMaxPrice(value);
      resetLists();
    }
  };

  const isLoadingData = isValidating || isDirValidating || isNewSearch;
  // Esqueletos solo en la primera carga; después, la lista anterior queda atenuada mientras llega la nueva.
  const showFullSkeleton = !data && !error;
  const isRefreshing = Boolean(data) && isLoadingData && size === 1 && dirSize === 1;
  const isLoadingMore = (isValidating || isDirValidating) && !isRefreshing && interleavedItems.length > 0;

  // Empty state only when BOTH sources return 0
  const bothEmpty = visibleVendors.length === 0 && directoryData.length === 0;

  // Resumen de la búsqueda en la barra del teléfono: dónde y qué.
  const whereLabel = locationLabel || (lat && lng ? 'Zona elegida' : 'Ciudad de Buenos Aires');
  const whatLabel = query ? `“${query}”` : selectedCategory ? categoryLabel(selectedCategory.name) : 'Todo';

  if (error)
    return (
      <MainLayout hasFooter={false}>
        <section className="flex items-center justify-center h-[calc(100dvh-72px)] bg-white">
          <div className="text-center px-6 fade-in">
            <h2 className="text-[17px] font-bold text-[var(--ink)] mb-2">No pudimos cargar los negocios</h2>
            <p className="text-sm text-slate-500 font-medium">Volvé a intentar en unos segundos.</p>
          </div>
        </section>
      </MainLayout>
    );

  return (
    <MainLayout hasFooter={false}>
      <section
        className={clsx(
          'explore',
          showMapOnPhone ? 'is-map' : 'is-list',
          // El buscador a pantalla completa del teléfono va por encima de /negocios.
          showSearchFullScreen && 'is-behind'
        )}
      >
        {/* Mapa: todo el fondo en escritorio; en el teléfono, una capa que se abre sobre la lista */}
        <section
          ref={mapPanelRef}
          aria-label="Mapa de resultados"
          className={clsx('map-panel', showMapOnPhone && 'is-open')}
        >
          {isMapMounted && (
            <MapView
              pins={pins}
              vendorsById={vendorsById}
              leftInset={isSplit ? PANEL_INSET : 0}
              // En el teléfono la barra de arriba (volver, búsqueda, vista y filtros) tapa ese alto.
              topInset={isSplit ? DESKTOP_BAR_HEIGHT : PHONE_BAR_HEIGHT}
              onBoundsChanged={handleBoundsChanged}
              isLoading={isLoadingData}
              selectedItemId={selectedItemId}
              onPinSelect={handlePinSelect}
            />
          )}
        </section>

        {/* Teléfono: arriba, volver + qué y dónde; después la vista y los filtros */}
        <div className="explore-bar md:hidden">
          <div className="explore-bar__top">
            <button type="button" className="explore-bar__back" aria-label="Volver" onClick={goBack}>
              <ChevronLeftIcon className="h-[18px] w-[18px]" strokeWidth={2.2} />
            </button>
            <button
              type="button"
              className="explore-bar__summary"
              onClick={() => setShowSearchFullScreen(true)}
              aria-label={`Buscar. Ahora: ${whatLabel} en ${whereLabel}`}
            >
              <b>{whereLabel}</b>
              <span>{whatLabel}</span>
            </button>
          </div>
          <div className="explore-bar__row">
            <ViewTabs view={view} onSelect={selectView} className="seg--dark explore-bar__tabs" />
            <TodayChip active={todayOnly} onToggle={filterProps.onToggleToday} />
          </div>
          <FilterChips {...filterProps} withAll withToday={false} className="explore-bar__chips" />
        </div>

        {/* Panel de resultados: flota sobre el mapa en escritorio */}
        <aside className="explore__panel" aria-label="Resultados">
          <div className="explore__head hidden md:block">
            <ViewTabs view={view} onSelect={selectView} />
            <FilterChips {...filterProps} className="explore__chips" />
          </div>

          <div ref={scrollContainerRef} className="explore__scroll @container">
            {view === 'trabajos' ? (
              <>
                <p className="explore__note">Trabajos de negocios en esta zona del mapa</p>
                <WorksGrid
                  boundsParams={boundsParams}
                  query={query}
                  categoryId={categoryId}
                  todayOnly={todayOnly}
                  selectedId={selectedItemId}
                  scrollRoot={scrollContainerRef}
                />
              </>
            ) : (
              <>
                <ResultsHeader
                  vendorCount={totalAvailableVendors}
                  directoryCount={totalAvailableDir}
                  query={query}
                  isValidating={isLoadingData}
                />

                {/* Full skeleton grid -- first load only */}
                {showFullSkeleton && (
                  <div className="results-grid">
                    {[...Array(6)].map((_, i) => (
                      <SkeletonBusiness key={`skeleton-${i}`} index={i} />
                    ))}
                  </div>
                )}

                {/* Empty state -- only when truly no results from both sources */}
                {!showFullSkeleton && bothEmpty && !isLoadingData && <EmptyState query={query} />}

                {/* Negocios de Ágora y fichas del directorio, por cercanía */}
                {!showFullSkeleton && interleavedItems.length > 0 && (
                  <div
                    className={clsx('results-grid', isRefreshing && 'is-refreshing')}
                    aria-busy={isRefreshing}
                  >
                    {interleavedItems.map((item, i) => (
                      <div
                        key={item._compositeId}
                        ref={(el) => {
                          if (el) cardRefsMap.current.set(item._compositeId, el);
                          else cardRefsMap.current.delete(item._compositeId);
                        }}
                        onMouseEnter={highlight(item._compositeId)}
                        onMouseLeave={clearHighlight}
                        className={clsx(
                          'result-cell fade-in',
                          selectedItemId === item._compositeId && 'is-selected'
                        )}
                      >
                        {item.source === 'agora' ? (
                          <VendorCard item={item} priority={i < 2} />
                        ) : (
                          <DirectoryCard item={item} />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Loading more (infinite scroll) */}
                {isLoadingMore && (
                  <div className="results-grid pt-0">
                    {[...Array(2)].map((_, i) => (
                      <SkeletonBusiness key={`skeleton-more-${i}`} index={i} />
                    ))}
                  </div>
                )}

                {/* Infinite scroll sentinel */}
                <div ref={loaderRef} className="h-5" />

                {/* End of results -- show when both sources are exhausted */}
                {isReachingEnd && interleavedItems.length > 0 && !isLoadingData && <EndOfResults />}
              </>
            )}

            {/* Espacio para que el botón flotante del teléfono no tape la última tarjeta */}
            <div className="h-24 md:hidden" aria-hidden="true" />
          </div>
        </aside>

        <button
          type="button"
          className={clsx('map-toggle md:hidden', showMapOnPhone && 'is-map')}
          onClick={toggleMapOnPhone}
          aria-pressed={showMapOnPhone}
        >
          {showMapOnPhone ? (
            <>
              <ListBulletIcon className="h-4 w-4" />
              Ver lista
            </>
          ) : (
            <>
              <MapIcon className="h-4 w-4" />
              Ver mapa
            </>
          )}
        </button>
      </section>
    </MainLayout>
  );
};

export default NegociosClient;
